/**
 * Upload Portal — one auto-detecting button (drag-drop), no template picker.
 *
 * The operator drops a file; the backend reads which template it is off the
 * header row and which reporting month it covers off the period column
 * (`POST /uploads`, multipart with the file alone). A file that matches no
 * template is refused with a header diff and nothing is stored. The old
 * six-card portal made the operator answer three questions the file already
 * answered — which card, which period, which format — and punished a wrong
 * card with a `column_structure` error.
 *
 * What remains beside the button:
 *   - Calibration certificates keep their own explicitly-labelled control:
 *     the one template with no period column, so the button cannot carry it
 *     (it posts to the typed route with the selected period, as before).
 *   - The reporting-period selector is a VIEW FILTER only — it scopes the
 *     uploads table below and supplies the calibration control's period. The
 *     button itself is never blocked on a selection.
 *   - Row-level parse errors still render inline: they exist only on the POST
 *     response (the GET detail page reads metadata), so this is their one
 *     chance to be seen.
 *
 * Upload progress: `fetch()` doesn't expose upload-progress events natively,
 * and nothing here requires a progress bar — the Dropzone loading state covers
 * the in-flight POST. True progress UI needs XMLHttpRequest; deferred until a
 * ticket actually requires it.
 */

import { isApiError } from "@compliance/domain";
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
  Title,
} from "@mantine/core";
import { Dropzone, type FileRejection } from "@mantine/dropzone";
import { notifications } from "@mantine/notifications";
import {
  IconAlertCircle,
  IconCheck,
  IconChevronRight,
  IconCircleCheckFilled,
  IconCloudUpload,
  IconCopyCheck,
  IconFileSearch,
  IconFileText,
  IconShieldX,
  IconUpload,
  IconX,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  useBatches,
  useEquipmentRoster,
  useExtractCalibrationCert,
  useFileCalibrationCert,
  useUploadAutoFile,
  useUploads,
} from "../api/hooks";
import type {
  EquipmentRosterItem,
  ManualUpload,
  UploadIngestResult,
  UploadStatus,
  UploadType,
} from "../api/types";
import { useUploadTypeLabels } from "../auth/useBranding";
import { pickActiveBatch } from "../lib/batchState";
import { formatPacificDisplay, periodLabelFrom } from "../lib/datetime";
import { formatApiError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import {
  AUTO_DETECT_ACCEPT,
  AUTO_DETECT_MAX_MB,
  UPLOAD_STATUS_COLOR,
  UPLOAD_TYPE_LABEL,
  UPLOAD_TYPE_META,
  ingestFailed,
} from "../lib/uploadTypes";
import {
  type DuplicateUploadConflict,
  DuplicateUploadPrompt,
} from "./uploads/DuplicateUploadPrompt";
import { UploadRowErrorList } from "./uploads/UploadRowErrorList";
import { UploadsTabs } from "./uploads/UploadsTabs";

// ── Status chip ────────────────────────────────────────────────────────────

function StatusChip({ status }: { status: UploadStatus }) {
  return (
    <Badge color={UPLOAD_STATUS_COLOR[status]} variant="light" size="sm">
      {status}
    </Badge>
  );
}

/**
 * Status icon for an upload row. A green check is only justified for
 * terminal-clean states (parsed, accepted). Other statuses use icons that
 * match their semantic — a superseded upload isn't current, a `received`
 * upload failed to parse (no spinner — it's not in-flight), a rejected
 * upload failed.
 */
function RowStatusIcon({ status }: { status: UploadStatus }) {
  const color = UPLOAD_STATUS_COLOR[status];
  if (status === "rejected") {
    return (
      <ThemeIcon variant="light" color={color} size={24} radius="xl">
        <IconX size={14} />
      </ThemeIcon>
    );
  }
  if (status === "superseded") {
    return (
      <ThemeIcon variant="light" color={color} size={24} radius="xl">
        <IconCopyCheck size={14} />
      </ThemeIcon>
    );
  }
  if (status === "received") {
    // Parse failed (parse_ok=false) — needs fix-and-reupload, not a spinner.
    return (
      <ThemeIcon variant="light" color={color} size={24} radius="xl">
        <IconAlertCircle size={14} />
      </ThemeIcon>
    );
  }
  return (
    <ThemeIcon variant="light" color={color} size={24} radius="xl">
      <IconCheck size={14} />
    </ThemeIcon>
  );
}

/**
 * Build the notification config for an upload response. The HTTP status was
 * 2xx — `parse_ok` / `status` tell the story. (Duplicate bytes return a 409
 * conflict and detection failures a 400, so neither reaches here — see
 * onDrop's catch.) When the backend detected the template (`detected`), the
 * toast names what it decided — that IS the feedback loop for the one-button
 * flow, so it leads with the detected type + period rather than the filename.
 */
export function toastForResult(
  result: UploadIngestResult,
  labels: Record<UploadType, string> = UPLOAD_TYPE_LABEL,
): {
  title: string;
  message: string;
  color: string;
  icon: React.ReactNode;
} {
  const label = labels[result.upload_type];
  const rows = `${result.rows_parsed} row${result.rows_parsed === 1 ? "" : "s"}`;
  if (ingestFailed(result)) {
    return {
      title: "Upload rejected — fix and re-upload",
      message: `${result.filename} (${label} · ${result.period}): ${
        result.row_errors.length
      } error${result.row_errors.length === 1 ? "" : "s"}`,
      color: UPLOAD_STATUS_COLOR.rejected,
      icon: <IconX size={18} />,
    };
  }
  const subject = result.detected ? `Detected ${label} for ${result.period}` : result.filename;
  // "parsed" ≠ "accepted" — a clean parse still awaits reviewer acceptance
  // (POST /uploads/{id}/accept) before the rows join the batch dataset.
  const isAccepted = result.status === "accepted";
  return {
    title: isAccepted ? "Upload accepted" : "Upload parsed",
    message: isAccepted
      ? `${subject} — ${rows} applied to the batch dataset.`
      : `${subject} — ${rows} parsed cleanly; awaiting reviewer acceptance.`,
    color: UPLOAD_STATUS_COLOR[result.status],
    icon: <IconCircleCheckFilled size={18} />,
  };
}

// ── Detection failures ─────────────────────────────────────────────────────

/** The 400 envelope from POST /uploads — `details.detection` marks it. */
export type DetectionFailure = {
  message: string;
  details: Record<string, unknown>;
};

const strings = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];

/**
 * Render the structured header diff a detection refusal carries: what the
 * file's header was, the closest template, and exactly which columns are
 * missing or unexpected — so the operator fixes the file, not their guess.
 */
/** Title + guidance per refusal code — several refusals are NOT header
 *  mismatches (a recognized file spanning two months, a calibration cert on
 *  the main dropzone), and announcing those as "no template matches" would
 *  flatly contradict the message body. */
function detectionCopy(code: string): { title: string; guidance: string } {
  const fix =
    "Nothing was stored. Fix the file and drop it again — blank templates are on the Templates tab.";
  if (code === "unrecognized_template") {
    return { title: "No template matches this file", guidance: fix };
  }
  if (code === "template_not_detectable") {
    return {
      title: "This document has its own upload control",
      guidance: "Nothing was stored. Use the dedicated control below instead.",
    };
  }
  if (code === "template_gated") {
    // The file is right; the platform just hasn't switched this template on.
    // Deliberately not the "fix the file" guidance — there is nothing to fix,
    // and telling someone to correct a correct file is worse than saying
    // nothing.
    return {
      title: "This template isn't enabled yet",
      guidance:
        "Nothing was stored, and there's nothing wrong with the file — the platform " +
        "recognises this template but can't accept it until it's switched on.",
    };
  }
  if (code === "period_missing" || code === "period_invalid" || code === "period_ambiguous") {
    return { title: "The reporting month can't be read from this file", guidance: fix };
  }
  if (code === "empty_file") {
    return { title: "The file is empty", guidance: fix };
  }
  return { title: "The file can't be read", guidance: fix };
}

function DetectionFailureAlert({ failure }: { failure: DetectionFailure }) {
  const labels = useUploadTypeLabels();
  const details = failure.details;
  const observed = strings(details.observed_header);
  const missing = strings(details.missing_columns);
  const unexpected = strings(details.unexpected_columns);
  const closestType =
    typeof details.closest_template === "string" ? (details.closest_template as UploadType) : null;
  const closest = closestType ? (labels[closestType] ?? closestType) : null;
  const copy = detectionCopy(typeof details.detection === "string" ? details.detection : "");
  return (
    <Alert color="red" variant="light" icon={<IconFileSearch size={16} />} title={copy.title}>
      <Stack gap={6}>
        <Text size="sm">{failure.message}</Text>
        {closest ? (
          <Text size="xs">
            Closest template:{" "}
            <Text span fw={600}>
              {closest}
            </Text>
          </Text>
        ) : null}
        {missing.length > 0 ? (
          <Group gap={6} wrap="wrap">
            <Text size="xs" c="dimmed">
              Missing columns:
            </Text>
            {missing.map((column) => (
              <Code key={column} c="red">
                {column}
              </Code>
            ))}
          </Group>
        ) : null}
        {unexpected.length > 0 ? (
          <Group gap={6} wrap="wrap">
            <Text size="xs" c="dimmed">
              Not in the template:
            </Text>
            {unexpected.map((column) => (
              <Code key={column}>{column}</Code>
            ))}
          </Group>
        ) : null}
        {observed.length > 0 ? (
          <Group gap={6} wrap="wrap">
            <Text size="xs" c="dimmed">
              Your file's header:
            </Text>
            {/* One joined chip, not one per column: an observed header can
                legitimately repeat a name, which is exactly the kind of
                mistake this diff exists to show. */}
            <Code>{observed.join(", ")}</Code>
          </Group>
        ) : null}
        <Text size="xs" c="dimmed">
          {copy.guidance}
        </Text>
      </Stack>
    </Alert>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

export function UploadsPage() {
  const batchesQ = useBatches();
  const batches = batchesQ.data?.items ?? [];

  // The period selector is a view filter (and the calibration control's
  // period source) — it no longer gates uploading. Defaults to the active
  // batch (newest Open, else newest overall). Now that it is a filter, the
  // selection lives in the URL (repo convention: filters → searchParams, as
  // UploadHistory already does), so a reload or a shared link reproduces the
  // view; the default stays derived rather than written back.
  const defaultBatch = useMemo(() => pickActiveBatch(batches)?.batch_id ?? null, [batches]);
  const [params, setParams] = useSearchParams();
  const selectedBatchId = params.get("batch");
  const setSelectedBatchId = (value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set("batch", value);
    else next.delete("batch");
    setParams(next, { replace: true });
  };
  const batchId = selectedBatchId ?? defaultBatch;
  const selectedBatch = batches.find((b) => b.batch_id === batchId);
  const period = selectedBatch ? selectedBatch.period_start.slice(0, 7) : undefined;

  const uploadsQ = useUploads(period);
  const uploads = uploadsQ.data?.items ?? [];

  // When a POST returns 409 DUPLICATE_UPLOAD_CONFLICT the conflict surfaces
  // here so the replace drawer renders alongside the page. The incoming File
  // is held in state so Replace can re-POST it.
  const [conflict, setConflict] = useState<DuplicateUploadConflict | null>(null);

  return (
    <Stack gap="lg">
      <Stack gap={4}>
        <Group gap="sm" align="center" justify="space-between" wrap="wrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="indigo" size={36} radius="md">
              <IconCloudUpload size={20} />
            </ThemeIcon>
            <Title order={2}>Upload data</Title>
          </Group>
          <UploadsTabs />
        </Group>
        <Text c="dimmed" size="sm" maw={720}>
          Drop a file — the platform detects which template it is from the column headers and reads
          the reporting month from the file itself. A file that matches no template is refused with
          a diff, and nothing is guessed. Templates pre-fill the period and site list; find them on
          the Templates tab.
        </Text>
      </Stack>

      <AutoUploadDropzone onDuplicateConflict={setConflict} />

      <Paper p="md" radius="md" withBorder>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <Group gap="md" wrap="nowrap">
            <Text size="sm" fw={500}>
              Reporting period
            </Text>
            <Select
              w={320}
              data={batches.map((b) => ({
                value: b.batch_id,
                label: `${b.period_label ?? periodLabelFrom(b.period_start)} · ${b.state} · ${b.batch_id}`,
              }))}
              value={batchId}
              onChange={setSelectedBatchId}
              placeholder={batchesQ.isPending ? "Loading…" : "Select a batch"}
              disabled={batchesQ.isPending}
            />
            <Text size="xs" c="dimmed" visibleFrom="sm">
              Filters the list below; uploading never waits on it.
            </Text>
          </Group>
          {/* The query is disabled without a period (isPending forever), and an
              error is not an empty period — only a settled success may claim a
              count. */}
          {!period ? null : uploadsQ.isPending ? (
            <Loader size="sm" />
          ) : uploadsQ.isError ? // The failure is reported once, below, where the list would have
          // been — two stacked "couldn't load" lines read as two problems.
          null : (
            <Text size="xs" c="dimmed">
              {uploads.length} upload{uploads.length === 1 ? "" : "s"} for this period
            </Text>
          )}
        </Group>
      </Paper>

      <CalibrationCertCard period={period} />

      {!period ? (
        <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
          Pick a reporting period above to see its uploads.
        </Alert>
      ) : uploadsQ.isPending ? (
        <Group justify="center" p="lg">
          <Loader size="sm" />
        </Group>
      ) : uploadsQ.isError ? (
        // Never the table: its empty state says "no uploads for this period",
        // and an operator who reads that after a failed load can reasonably
        // conclude nothing was ever submitted and send the month's figures
        // again. A failure to load is not a count of zero.
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          <Text size="sm">
            Couldn't load this period's uploads ({formatApiError(uploadsQ.error)}). This is not the
            same as the period having none — retry before re-submitting anything.
          </Text>
        </Alert>
      ) : (
        <UploadsForPeriod uploads={uploads} period={period} />
      )}

      <DuplicateUploadPrompt
        // Keying on the conflict object identity forces a fresh component
        // instance per conflict — local reason/error state resets without an
        // effect.
        key={conflict ? `${conflict.existingUploadId}:${conflict.incomingSha256}` : "none"}
        conflict={conflict}
        opened={conflict !== null}
        onClose={() => setConflict(null)}
      />
    </Stack>
  );
}

// ── The one button ─────────────────────────────────────────────────────────

/** Client-side Dropzone rejection (wrong extension / oversize): toast it.
 *  Shared by both dropzones — a silently swallowed drop reads as a successful
 *  upload to the operator, which is worse than any error. */
function notifyRejected(rejections: FileRejection[]) {
  const first = rejections[0];
  if (!first) return;
  const reason = first.errors[0]?.message ?? "Rejected";
  notifications.show({
    title: "File rejected",
    message: `${first.file.name}: ${reason}`,
    color: "red",
    autoClose: 4500,
  });
}

/** Build the replace-drawer payload from a 409's details, when they name the
 *  incumbent's slot. The auto route's client never declared a type or period,
 *  so both must come from the envelope (the BE includes them since the
 *  one-button change); without them the replace flow has nothing to aim at. */
function conflictFromDetails(
  details: Record<string, unknown>,
  file: File,
): DuplicateUploadConflict | null {
  const uploadType = details.upload_type;
  const period = details.period;
  if (typeof uploadType !== "string" || typeof period !== "string") return null;
  if (!(uploadType in UPLOAD_TYPE_META)) return null;
  return {
    existingUploadId: String(details.existing_upload_id ?? ""),
    existingSha256: String(details.existing_sha256 ?? ""),
    incomingSha256: String(details.incoming_sha256 ?? ""),
    incomingFile: file,
    uploadType: uploadType as UploadType,
    period,
  };
}

function AutoUploadDropzone({
  onDuplicateConflict,
}: {
  onDuplicateConflict: (conflict: DuplicateUploadConflict) => void;
}) {
  const uploadMutation = useUploadAutoFile();
  const queryClient = useQueryClient();
  const labels = useUploadTypeLabels();
  const [lastResult, setLastResult] = useState<UploadIngestResult | null>(null);
  const [detectionFailure, setDetectionFailure] = useState<DetectionFailure | null>(null);
  const [lastError, setLastError] = useState<{ code: string; message: string } | null>(null);

  const onDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    // Clear every previous outcome so a stale success row doesn't sit next to
    // a new error alert (and vice versa).
    setLastError(null);
    setDetectionFailure(null);
    setLastResult(null);
    try {
      const result = await uploadMutation.mutateAsync({
        file,
        // The type isn't known client-side any more; the response reports what
        // was detected, and the audit trail carries the filename.
        // Any filename travels: apiFetch encodes a header-unsafe reason (RFC 8187).
        reason: `Upload ${file.name}`,
      });
      setLastResult(result);
      // Fire-and-forget: everything the toast and result row render is already
      // in `result`; awaiting the refetch would hold the feedback hostage to a
      // second round-trip.
      void queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all });
      const toast = toastForResult(result, labels);
      notifications.show({
        title: toast.title,
        message: toast.message,
        color: toast.color,
        icon: toast.icon,
        autoClose: 4500,
      });
    } catch (err) {
      if (isApiError(err)) {
        if (err.code === "DUPLICATE_UPLOAD_CONFLICT" && err.details) {
          const conflict = conflictFromDetails(err.details, file);
          if (conflict) {
            onDuplicateConflict(conflict);
            return;
          }
        }
        // A detection refusal is a 400 whose details carry `detection` — give
        // it the structured diff treatment instead of a bare code+message.
        if (err.details && typeof err.details.detection === "string") {
          setDetectionFailure({ message: err.message, details: err.details });
          return;
        }
        setLastError({ code: err.code, message: err.message });
      } else {
        setLastError({ code: "UNKNOWN", message: (err as Error).message });
      }
    }
  };

  return (
    <Stack gap="sm">
      <Dropzone
        data-tour="upload-dropzone"
        onDrop={onDrop}
        onReject={notifyRejected}
        accept={AUTO_DETECT_ACCEPT}
        maxFiles={1}
        maxSize={AUTO_DETECT_MAX_MB * 1024 * 1024}
        loading={uploadMutation.isPending}
        radius="md"
      >
        <Stack align="center" gap="sm" mih={140} justify="center" style={{ pointerEvents: "none" }}>
          <Dropzone.Accept>
            <ThemeIcon variant="light" color="teal" size={56} radius="xl">
              <IconUpload size={28} />
            </ThemeIcon>
          </Dropzone.Accept>
          <Dropzone.Reject>
            <ThemeIcon variant="light" color="red" size={56} radius="xl">
              <IconX size={28} />
            </ThemeIcon>
          </Dropzone.Reject>
          <Dropzone.Idle>
            <ThemeIcon variant="light" color="indigo" size={56} radius="xl">
              <IconCloudUpload size={28} />
            </ThemeIcon>
          </Dropzone.Idle>
          <Stack gap={2} align="center">
            <Text size="md" fw={600}>
              Drop any data file here, or click to browse
            </Text>
            <Text size="xs" c="dimmed">
              The template and reporting month are detected from the file · .csv, .xlsx · max{" "}
              {AUTO_DETECT_MAX_MB} MB
            </Text>
          </Stack>
        </Stack>
      </Dropzone>

      {detectionFailure ? <DetectionFailureAlert failure={detectionFailure} /> : null}
      {lastError ? <UploadErrorAlert error={lastError} /> : null}

      {/* row_errors only exist on this ingest response — the detail page reads
          metadata only and can't show them. Render them inline so the operator
          can fix the file before the response is gone. */}
      {lastResult ? (
        <Stack gap="xs">
          <UploadResultRow view={fromIngest(lastResult, labels)} highlighted />
          {lastResult.row_errors.length > 0 ? (
            <UploadRowErrorList errors={lastResult.row_errors} />
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}

// ── Calibration certificates (the one non-detectable template) ─────────────

/**
 * The due date an instrument's own interval implies, or `null` when it has none.
 *
 * A suggestion, not a rule: a lab may certify for a different term, so the field
 * stays editable and this never overwrites a value already in it. Computed in UTC
 * so it cannot drift a day either side of midnight in the operator's timezone —
 * these are calendar dates, and a day's error is the difference between a
 * certificate covering a period and leaving its last day uncalibrated.
 */
export function suggestNextDue(calibratedOn: string, intervalDays: number | null): string | null {
  if (!calibratedOn || !intervalDays) return null;
  const due = new Date(`${calibratedOn}T00:00:00Z`);
  if (Number.isNaN(due.getTime())) return null;
  due.setUTCDate(due.getUTCDate() + intervalDays);
  return due.toISOString().slice(0, 10);
}

/**
 * File a lab-issued calibration certificate.
 *
 * The PDF alone cannot clear anything: coverage is judged from
 * `calibrated_on`/`next_due_on` against every reported day of a batch period, and
 * those dates live on the certificate rather than in the file. So the drop holds
 * the file and reveals the fields, and one submit records the calibration and
 * closes whatever findings it covers.
 *
 * (Before this the card uploaded straight from the drop, into a backend that
 * refused PDFs outright — the control had never been able to succeed.)
 */
function CalibrationCertCard({ period }: { period: string | undefined }) {
  const meta = UPLOAD_TYPE_META.calibration_cert;
  const fileMutation = useFileCalibrationCert();
  const extractMutation = useExtractCalibrationCert();
  const rosterQ = useEquipmentRoster();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [tag, setTag] = useState<string | null>(null);
  const [calibratedOn, setCalibratedOn] = useState("");
  const [nextDueOn, setNextDueOn] = useState("");
  const [certificateNumber, setCertificateNumber] = useState("");
  const [issuer, setIssuer] = useState("");
  const [lastError, setLastError] = useState<{ code: string; message: string } | null>(null);
  const [readNote, setReadNote] = useState<string | null>(null);

  const roster = rosterQ.data ?? [];
  const acceptedExts = Object.values(meta.accept).flat().join(", ");
  const ready = Boolean(file && tag && calibratedOn && nextDueOn);

  const reset = () => {
    setFile(null);
    setTag(null);
    setCalibratedOn("");
    setNextDueOn("");
    setCertificateNumber("");
    setIssuer("");
    setReadNote(null);
  };

  /**
   * Read the dropped certificate and pre-fill what it says.
   *
   * Best-effort by design: the fields stay editable, the operator confirms them,
   * and a failure here leaves a usable form rather than a blocked one. Nothing is
   * uploaded by this call — the filing still goes through the submit below, which
   * is where the validation and the audit trail are.
   */
  const onDrop = async (files: File[]) => {
    const dropped = files[0];
    if (!dropped) return;
    setLastError(null);
    setReadNote(null);
    setFile(dropped);
    try {
      const read = await extractMutation.mutateAsync({ file: dropped });
      if (read.equipment_tag) setTag(read.equipment_tag);
      if (read.calibrated_on) setCalibratedOn(read.calibrated_on);
      if (read.next_due_on) setNextDueOn(read.next_due_on);
      if (read.certificate_number) setCertificateNumber(read.certificate_number);
      if (read.certificate_issuer) setIssuer(read.certificate_issuer);
      setReadNote(read.note);
    } catch {
      setReadNote("Could not read the certificate — enter its details below.");
    }
  };

  const onCalibratedOn = (value: string) => {
    setCalibratedOn(value);
    if (nextDueOn) return; // never overwrite a date the operator typed
    const days = roster.find((i: EquipmentRosterItem) => i.tag === tag)?.calibration_interval_days;
    const suggested = suggestNextDue(value, days ?? null);
    if (suggested) setNextDueOn(suggested);
  };

  const onSubmit = async () => {
    if (!file || !tag || !calibratedOn || !nextDueOn) return;
    setLastError(null);
    try {
      const result = await fileMutation.mutateAsync({
        file,
        reason: `File calibration certificate for ${tag}`,
        equipment_tag: tag,
        calibrated_on: calibratedOn,
        next_due_on: nextDueOn,
        certificate_number: certificateNumber || undefined,
        certificate_issuer: issuer || undefined,
      });
      // The registry moved, so the approval gate's verdict and the findings list
      // both change — not just the uploads list.
      void queryClient.invalidateQueries({ queryKey: queryKeys.uploads.all });
      // batches.all covers each batch detail's issues and calibration-readiness
      // keys, which are nested under it.
      void queryClient.invalidateQueries({ queryKey: queryKeys.batches.all });
      void queryClient.invalidateQueries({ queryKey: ["equipment"] });
      const cleared = result.resolved_issue_ids.length;
      notifications.show({
        title: "Calibration filed",
        message: cleared
          ? `${cleared} finding${cleared === 1 ? "" : "s"} cleared for ${tag}.`
          : `Recorded for ${tag}. It covers no open finding's period, so nothing cleared.`,
        color: cleared ? "teal" : "blue",
        autoClose: 6000,
      });
      reset();
    } catch (err) {
      setLastError({
        code: isApiError(err) ? err.code : "",
        message: formatApiError(err),
      });
    }
  };

  return (
    <Card withBorder radius="md" padding="md" data-tour="calibration-cert-card">
      <Stack gap="sm">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" color="indigo" radius="md" size={36}>
            <IconFileText size={18} />
          </ThemeIcon>
          <Stack gap={2}>
            <Text fw={600} size="sm">
              {meta.label}
            </Text>
            <Text size="xs" c="dimmed">
              {meta.description}
            </Text>
          </Stack>
        </Group>

        {file ? (
          <Group gap="xs" wrap="nowrap">
            <ThemeIcon variant="light" color="teal" size={28} radius="xl">
              <IconFileText size={14} />
            </ThemeIcon>
            <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
              {file.name}
            </Text>
            {extractMutation.isPending ? (
              <Group gap={6} wrap="nowrap">
                <Loader size={14} />
                <Text size="xs" c="dimmed">
                  Reading…
                </Text>
              </Group>
            ) : null}
            <Button size="compact-xs" variant="subtle" onClick={reset}>
              Choose another
            </Button>
          </Group>
        ) : (
          <Dropzone
            onDrop={onDrop}
            onReject={notifyRejected}
            accept={meta.accept}
            maxFiles={1}
            maxSize={meta.maxSizeMb * 1024 * 1024}
            disabled={!period}
            radius="md"
          >
            <Group justify="center" gap="md" mih={64} style={{ pointerEvents: "none" }}>
              <Dropzone.Accept>
                <ThemeIcon variant="light" color="teal" size={36} radius="xl">
                  <IconUpload size={18} />
                </ThemeIcon>
              </Dropzone.Accept>
              <Dropzone.Reject>
                <ThemeIcon variant="light" color="red" size={36} radius="xl">
                  <IconX size={18} />
                </ThemeIcon>
              </Dropzone.Reject>
              <Dropzone.Idle>
                <ThemeIcon variant="light" color="gray" size={36} radius="xl">
                  <IconCloudUpload size={18} />
                </ThemeIcon>
              </Dropzone.Idle>
              <Stack gap={0}>
                <Text size="sm" fw={500}>
                  {period
                    ? "Drop a certificate"
                    : "Select a reporting period above to upload a certificate"}
                </Text>
                <Text size="xs" c="dimmed">
                  {acceptedExts} · max {meta.maxSizeMb} MB
                </Text>
              </Stack>
            </Group>
          </Dropzone>
        )}

        {file ? (
          <Stack gap="xs">
            <Select
              label="Instrument"
              placeholder={rosterQ.isPending ? "Loading…" : "Pick the instrument"}
              data={roster.map((i: EquipmentRosterItem) => ({
                value: i.tag,
                label: i.facility_identifier
                  ? `${i.tag} · ${i.kind} · ${i.facility_identifier}`
                  : `${i.tag} · ${i.kind}`,
              }))}
              value={tag}
              onChange={setTag}
              searchable
              size="sm"
            />
            <Group grow gap="xs">
              <TextInput
                label="Calibrated on"
                type="date"
                value={calibratedOn}
                onChange={(e) => onCalibratedOn(e.currentTarget.value)}
                size="sm"
              />
              <TextInput
                label="Next due on"
                type="date"
                value={nextDueOn}
                onChange={(e) => setNextDueOn(e.currentTarget.value)}
                size="sm"
              />
            </Group>
            <Group grow gap="xs">
              <TextInput
                label="Certificate number"
                placeholder="optional"
                value={certificateNumber}
                onChange={(e) => setCertificateNumber(e.currentTarget.value)}
                size="sm"
              />
              <TextInput
                label="Issuer"
                placeholder="optional"
                value={issuer}
                onChange={(e) => setIssuer(e.currentTarget.value)}
                size="sm"
              />
            </Group>
            {readNote ? (
              <Alert variant="light" color="yellow" icon={<IconAlertCircle size={16} />} p="xs">
                <Text size="xs">{readNote}</Text>
              </Alert>
            ) : null}
            <Text size="xs" c="dimmed">
              Read from the certificate — check them before filing. It must cover every reported day
              of a period to clear that period's finding, so a calibration dated after the period
              began will not close it.
            </Text>
            <Group justify="flex-end">
              <Button
                size="sm"
                onClick={onSubmit}
                data-tour="calibration-file"
                loading={fileMutation.isPending}
                disabled={!ready}
              >
                File calibration
              </Button>
            </Group>
          </Stack>
        ) : null}

        {lastError ? <UploadErrorAlert error={lastError} /> : null}
      </Stack>
    </Card>
  );
}

// ── The period's uploads (replaces the per-card "Most recent" chips) ───────

/**
 * With the six cards gone, this table is the period's coverage view: every
 * record for the selected period, newest first, template named per row. The
 * per-card chip only ever showed one record per type; this shows the failed
 * attempts too, which is what an operator scanning "what still needs fixing"
 * actually wants.
 */
function UploadsForPeriod({ uploads, period }: { uploads: ManualUpload[]; period: string }) {
  const navigate = useNavigate();
  const labels = useUploadTypeLabels();
  const sorted = useMemo(
    () =>
      // Compare by instant, not lexically: `uploaded_at` is offset-aware ISO
      // 8601, so string ordering only matches chronological ordering when
      // every value shares the same offset.
      [...uploads].sort((a, b) => Date.parse(b.uploaded_at) - Date.parse(a.uploaded_at)),
    [uploads],
  );

  if (sorted.length === 0) {
    return (
      <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
        No uploads for {period} yet. Drop a file above to add the first one.
      </Alert>
    );
  }

  return (
    <Paper radius="md" withBorder>
      <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Template</Table.Th>
            <Table.Th>File</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Size</Table.Th>
            <Table.Th>Uploaded</Table.Th>
            <Table.Th aria-label="Open" />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {sorted.map((upload, index) => (
            <Table.Tr
              key={upload.id}
              style={{ cursor: "pointer" }}
              onClick={() => navigate(`/uploads/${upload.id}`)}
              data-tour={index === 0 ? "upload-row-first" : undefined}
            >
              <Table.Td>
                <Text size="sm" fw={500}>
                  {labels[upload.upload_type]}
                </Text>
              </Table.Td>
              <Table.Td>
                <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                  <RowStatusIcon status={upload.status} />
                  <Text size="sm" truncate maw={280}>
                    {upload.original_filename}
                  </Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <StatusChip status={upload.status} />
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {(upload.content_length / 1024).toFixed(1)} KB
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" c="dimmed">
                  {formatPacificDisplay(upload.uploaded_at)}
                </Text>
              </Table.Td>
              <Table.Td>
                <IconChevronRight size={16} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

// ── Inline result row (POST feedback) ──────────────────────────────────────

/**
 * Normalized row for the freshly-uploaded chip. Kept for the POST ingest
 * result, which carries parse feedback the stored record never will.
 */
type UploadRowView = {
  uploadId: string;
  filename: string;
  status: UploadStatus;
  /** The detected template, named so the operator can catch a wrong match. */
  typeLabel: string;
  period: string;
  rows: number | null;
  contentLength: number;
  errorCount: number | null;
};

const fromIngest = (r: UploadIngestResult, labels: Record<UploadType, string>): UploadRowView => ({
  uploadId: r.upload_id,
  filename: r.filename,
  status: r.status,
  typeLabel: labels[r.upload_type],
  period: r.period,
  rows: r.rows_parsed,
  contentLength: r.content_length,
  errorCount: r.row_errors.length,
});

function UploadResultRow({
  view,
  highlighted = false,
}: {
  view: UploadRowView;
  highlighted?: boolean;
}) {
  const meta = [
    `${view.typeLabel} · ${view.period}`,
    view.rows != null ? `${view.rows} rows` : null,
    `${(view.contentLength / 1024).toFixed(1)} KB`,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <Paper
      component={Link}
      to={`/uploads/${view.uploadId}`}
      p="xs"
      radius="sm"
      withBorder
      style={(t) => ({
        textDecoration: "none",
        color: "inherit",
        cursor: "pointer",
        borderColor: highlighted ? t.colors.indigo[3] : undefined,
        backgroundColor: highlighted ? t.colors.indigo[0] : undefined,
        transition: "border-color 120ms ease",
      })}
      className="upload-result-row"
    >
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
          <RowStatusIcon status={view.status} />
          <Stack gap={0} style={{ minWidth: 0 }}>
            <Text size="xs" fw={500} truncate>
              {view.filename}
            </Text>
            <Text size="xs" c="dimmed">
              {meta}
            </Text>
          </Stack>
        </Group>
        <StatusChip status={view.status} />
      </Group>
      {view.errorCount != null && view.errorCount > 0 ? (
        <Text size="xs" c="red" mt={4}>
          {view.errorCount} error{view.errorCount === 1 ? "" : "s"} · fix and re-upload
        </Text>
      ) : null}
    </Paper>
  );
}

function UploadErrorAlert({ error }: { error: { code: string; message: string } }) {
  const icon =
    error.code === "UPLOAD_MALWARE_DETECTED" ? (
      <IconShieldX size={16} />
    ) : (
      <IconAlertCircle size={16} />
    );
  return (
    <Alert color="red" variant="light" icon={icon} title={`${error.code}`}>
      <Text size="sm">{error.message}</Text>
    </Alert>
  );
}
